import React, { useState, useEffect } from 'react';
import { Eye, MessageSquare, Clock, CheckCircle, XCircle, AlertTriangle, Search, Filter, FileText, Play, Volume2, Image, Layers, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';
import Table, { ActionButton, ActionMenu, StatusBadge } from '../../components/common/Table';
import Modal, { ConfirmModal } from '../../components/common/Modal';
import apiService from '../../services/apiService';
import toast from 'react-hot-toast';

/**
 * Submissions Management Page
 * 
 * Comprehensive user submission management interface for administrators.
 * Allows viewing, updating status, and managing user-submitted reports and issues.
 */
const SubmissionsPage = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const [filters, setFilters] = useState({
    search: '',
    user_id: '',
    farm_id: '',
    observer_name: '',
    plot_id: '',
    start_date: '',
    end_date: '',
    page: 1,
    limit: 20,
  });

  // Modal states
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [statusNote, setStatusNote] = useState('');

  // Filter collapse state
  const [isFiltersCollapsed, setIsFiltersCollapsed] = useState(true);

  const columns = [
    {
      key: 'id',
      title: 'Submission',
      sortable: true,
      render: (submission) => (
        <div className="flex items-center">
          <div className={`p-2 rounded-lg mr-3 ${getHealthStatusColor(submission.plant_conditions)}`}>
            <FileText className="w-5 h-5" />
          </div>
          <div>
            <div className="font-medium text-secondary-900">
              {[submission.farm?.name, submission.plot?.name, submission.bed?.crop].filter(Boolean).join(' - ') || 'Unknown'}
            </div>
            <div className="text-sm text-secondary-500">
              {submission.bed?.id ? submission.bed.id.slice(0, 8) + '...' : submission.id.slice(0, 8) + '...'}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'observer_name',
      title: 'Observer',
      className: 'hidden md:table-cell',
      render: (submission) => (
        <div className="font-medium text-secondary-900">{submission.observer_name || 'Unknown'}</div>
      ),
    },
    {
      key: 'issue_types',
      title: 'Issue',
      sortable: false,
      className: 'hidden md:table-cell',
      render: (submission) => {
        if (submission.issue_types && submission.issue_types.length > 0) {
          return (
            <div className="flex flex-wrap gap-1">
              {submission.issue_types.map((issueType, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800"
                >
                  {issueType}
                </span>
              ))}
            </div>
          );
        }
        const healthStatus = getPlantHealthStatus(submission.plant_conditions);
        if (healthStatus.label === 'Not Assessed') {
          return <span></span>;
        }
        return (
          <StatusBadge
            status={healthStatus.label}
            variant={healthStatus.variant}
          />
        );
      },
    },
    {
      key: 'date',
      title: 'Date',
      sortable: true,
      render: (submission) => {
        const date = new Date(submission.date);
        return (
          <div className="font-medium text-secondary-900">{date.toLocaleDateString()}</div>
        );
      },
    },
    {
      key: 'social',
      title: 'Social',
      className: 'hidden lg:table-cell',
      render: (submission) => {
        if (!submission.social_post) return null;
        const statusMap = {
          posted: 'bg-green-100 text-green-700',
          pending: 'bg-yellow-100 text-yellow-700',
          partial: 'bg-orange-100 text-orange-700',
          failed: 'bg-red-100 text-red-700',
        };
        const statusClass = statusMap[submission.social_post_status] || 'bg-gray-100 text-gray-500';
        return (
          <div className="flex items-center gap-1.5">
            {submission.post_to_facebook && (
              <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${statusClass}`}>
                <img src="/facebook.png" alt="FB" className="w-3.5 h-3.5 mr-1" />
                FB
              </span>
            )}
            {submission.post_to_instagram && (
              <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${statusClass}`}>
                <img src="/instagram.png" alt="IG" className="w-3.5 h-3.5 mr-1" />
                IG
              </span>
            )}
            {submission.post_to_tiktok && (
              <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${statusClass}`}>
                <img src="/tiktok.png" alt="TK" className="w-3.5 h-3.5 mr-1" />
                TK
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: 'actions',
      title: 'Actions',
      width: '120px',
      render: (submission) => {
        const actions = [
          {
            key: 'status',
            label: 'Update Status',
            icon: MessageSquare,
            onClick: handleUpdateStatus,
          },
        ];
        if (submission.social_post && submission.post_to_facebook) {
          actions.push({
            key: 'repost-fb',
            label: 'Repost to Facebook',
            icon: RefreshCw,
            onClick: (sub) => handleRepostToSocialMedia(sub, 'facebook'),
          });
        }
        if (submission.social_post && submission.post_to_instagram) {
          actions.push({
            key: 'repost-ig',
            label: 'Repost to Instagram',
            icon: RefreshCw,
            onClick: (sub) => handleRepostToSocialMedia(sub, 'instagram'),
          });
        }
        if (submission.images?.length > 0 || submission.videos?.length > 0) {
          actions.push({
            key: 'share-tiktok-user',
            label: 'Share to TikTok',
            icon: RefreshCw,
            onClick: handleUserShareToTikTok,
          });
        }
        actions.push({
          key: 'delete',
          label: 'Delete Submission',
          icon: XCircle,
          variant: 'danger',
          onClick: handleDeleteSubmission,
        });
        return (
          <div className="flex space-x-2">
            <ActionButton
              icon={Eye}
              label="View Details"
              onClick={() => handleViewSubmission(submission)}
            />
            <ActionMenu item={submission} actions={actions} />
          </div>
        );
      },
    },
  ];

  useEffect(() => {
    loadSubmissions();
  }, [filters, sortConfig]);

  const loadSubmissions = async () => {
    try {
      setLoading(true);
      const params = {
        ...filters,
        sort_by: sortConfig.key,
        sort_direction: sortConfig.direction,
      };

      const response = await apiService.getSubmissions(params);

      console.log('Submissions response:', response);
      
      if (response.success) {
        setSubmissions(response.data.submissions || []);
        setPagination(response.data.pagination);
      } else {
        toast.error(response.message || 'Failed to load submissions');
      }
    } catch (error) {
      console.error('Error loading submissions:', error);
      toast.error('Failed to load submissions');
    } finally {
      setLoading(false);
    }
  };

  const getPlantHealthStatus = (plantConditions) => {
    if (!plantConditions) {
      return { label: 'Unknown', variant: 'default' };
    }
    
    if (plantConditions.Healthy) {
      return { label: 'Healthy', variant: 'success' };
    }
    
    if (plantConditions.Unhealthy) {
      return { label: 'Unhealthy', variant: 'danger' };
    }
    
    // Check for specific issues
    const issues = [];
    if (plantConditions['Signs of pest infestation']) issues.push('Pest');
    if (plantConditions['Signs of nutrient deficiency']) issues.push('Nutrient');
    if (plantConditions['Water stress (drought or flood)']) issues.push('Water Stress');
    if (plantConditions['Disease symptoms']) issues.push('Disease');
    if (plantConditions['Weed infestation']) issues.push('Weeds');
    
    if (issues.length > 0) {
      return { label: issues.join(', '), variant: 'warning' };
    }
    
    return { label: 'Not Assessed', variant: 'default' };
  };

  const getHealthStatusColor = (plantConditions) => {
    const status = getPlantHealthStatus(plantConditions);
    const colors = {
      'Healthy': 'bg-green-100 text-green-600',
      'Unhealthy': 'bg-red-100 text-red-600',
      'Unknown': 'bg-gray-100 text-gray-600',
      'Not Assessed': 'bg-gray-100 text-gray-600',
    };
    
    if (status.variant === 'warning') {
      return 'bg-yellow-100 text-yellow-600';
    }
    
    return colors[status.label] || 'bg-gray-100 text-gray-600';
  };

  const handleSort = (key, direction) => {
    setSortConfig({ key, direction });
  };

  const handlePageChange = (page) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleSearch = (value) => {
    setFilters(prev => ({ ...prev, search: value, page: 1 }));
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const handleViewSubmission = (submission) => {
    console.log(
      "Plot Name: ", submission.plot?.name,
      "Plot ID: ", submission.plot?.id,
      "Submission ID: ", submission.id,
      "Farm ID: ", submission.farm_id,
      "Bed ID: ", submission.bed_id,
      "Bed Name: ", submission.bed?.name,
      "Bed Size: ", submission.bed?.size,
      "Seed: ", submission.bed?.seed,
      "Submisison", submission
    );
    setSelectedSubmission(submission);
    setShowViewModal(true);
  };

  const handleUpdateStatus = (submission) => {
    setSelectedSubmission(submission);
    setNewStatus(submission.status);
    setStatusNote('');
    setShowStatusModal(true);
  };

  const handleDeleteSubmission = (submission) => {
    setSelectedSubmission(submission);
    setShowDeleteModal(true);
  };

  const handleRepostToSocialMedia = async (submission, platform) => {
    try {
      const response = await apiService.repostToSocialMedia(submission.id, platform);
      if (response.success) {
        toast.success(`Repost to ${platform} initiated`);
        loadSubmissions();
      } else {
        toast.error(response.message || `Failed to repost to ${platform}`);
      }
    } catch (error) {
      console.error(`Error reposting to ${platform}:`, error);
      toast.error(`Failed to repost to ${platform}`);
    }
  };

  const generatePKCE = async () => {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    const verifier = btoa(String.fromCharCode(...array))
      .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
    const data = new TextEncoder().encode(verifier);
    const digest = await crypto.subtle.digest('SHA-256', data);
    const challenge = btoa(String.fromCharCode(...new Uint8Array(digest)))
      .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
    return { verifier, challenge };
  };

  const handleUserShareToTikTok = async (submission) => {
    try {
      const { verifier, challenge } = await generatePKCE();
      localStorage.setItem('tiktok_pkce_verifier', verifier);
      const response = await apiService.getTikTokUserShareAuthURL(submission.id, challenge);
      const authURL = response.data?.auth_url;
      if (!authURL) {
        toast.error('Failed to get TikTok share URL');
        return;
      }

      const width = 600, height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      const popup = window.open(
        authURL,
        'tiktok-user-share',
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no`
      );

      const handleMessage = (event) => {
        if (event.data?.type !== 'tiktok-user-share-done') return;
        window.removeEventListener('message', handleMessage);
        clearInterval(pollTimer);
        if (event.data.success) {
          toast.success('Shared to TikTok!');
        } else {
          toast.error(event.data.error || 'Failed to share to TikTok');
        }
      };
      window.addEventListener('message', handleMessage);

      const pollTimer = setInterval(() => {
        if (popup?.closed) {
          clearInterval(pollTimer);
          window.removeEventListener('message', handleMessage);
        }
      }, 500);
    } catch (error) {
      console.error('Error sharing to TikTok:', error);
      toast.error('Failed to start TikTok share');
    }
  };

  const handleStatusUpdate = async () => {
    if (!selectedSubmission) return;

    try {
      setStatusLoading(true);
      
      const response = await apiService.updateSubmissionStatus(selectedSubmission.id, {
        status: newStatus,
        note: statusNote,
      });
      
      if (response.success) {
        toast.success('Submission status updated successfully');
        setShowStatusModal(false);
        loadSubmissions();
      } else {
        toast.error(response.message || 'Failed to update status');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    } finally {
      setStatusLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedSubmission) return;

    try {
      const response = await apiService.deleteSubmission(selectedSubmission.id);
      
      if (response.success) {
        toast.success('Submission deleted successfully');
        setShowDeleteModal(false);
        loadSubmissions();
      } else {
        toast.error(response.message || 'Failed to delete submission');
      }
    } catch (error) {
      console.error('Error deleting submission:', error);
      toast.error('Failed to delete submission');
    }
  };

  return (
    <div className="space-y-4 md:space-y-6 w-full max-w-full overflow-x-hidden">
      {/* Page Header */}
      {/* <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">User Submissions</h1>
          <p className="text-secondary-600">Manage user reports, issues, and feedback submissions</p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2 text-sm text-secondary-600">
            <div className="w-3 h-3 bg-red-500 rounded-full"></div>
            <span>Unhealthy: {submissions.filter(s => s.plant_conditions?.Unhealthy).length}</span>
          </div>
          <div className="flex items-center space-x-2 text-sm text-secondary-600">
            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
            <span>Healthy: {submissions.filter(s => s.plant_conditions?.Healthy).length}</span>
          </div>
          <div className="flex items-center space-x-2 text-sm text-secondary-600">
            <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
            <span>Submitted: {submissions.filter(s => s.status === 'submitted').length}</span>
          </div>
        </div>
      </div> */}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow mx-2 md:mx-0">
        {/* Filter Header */}
        <div className="flex items-center justify-between p-3 md:p-4 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <Filter className="w-4 h-4 md:w-5 md:h-5 text-secondary-500" />
            <h3 className="text-base md:text-lg font-medium text-secondary-900">Filters</h3>
          </div>
          <button
            onClick={() => setIsFiltersCollapsed(!isFiltersCollapsed)}
            className="flex items-center space-x-1 text-secondary-600 hover:text-secondary-900 transition-colors p-1"
          >
            <span className="text-xs md:text-sm">{isFiltersCollapsed ? 'Show' : 'Hide'}</span>
            {isFiltersCollapsed ? (
              <ChevronDown className="w-3 h-3 md:w-4 md:h-4" />
            ) : (
              <ChevronUp className="w-3 h-3 md:w-4 md:h-4" />
            )}
          </button>
        </div>

        {/* Collapsible Filter Content */}
        {!isFiltersCollapsed && (
          <div className="p-3 md:p-6">
            <div className="space-y-3 md:space-y-4">
              {/* First Row - Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 md:w-5 md:h-5 text-secondary-400" />
                <input
                  type="text"
                  placeholder="Search submissions..."
                  value={filters.search}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="form-input pl-8 md:pl-10 w-full text-sm md:text-base"
                />
              </div>

              {/* Second Row - Observer and Farm */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <input
                  type="text"
                  placeholder="Observer name..."
                  value={filters.observer_name}
                  onChange={(e) => handleFilterChange('observer_name', e.target.value)}
                  className="form-input w-full text-sm md:text-base"
                />
                <input
                  type="text"
                  placeholder="Farm ID..."
                  value={filters.farm_id}
                  onChange={(e) => handleFilterChange('farm_id', e.target.value)}
                  className="form-input w-full text-sm md:text-base"
                />
              </div>

              {/* Third Row - User and Plot */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <input
                  type="text"
                  placeholder="User ID..."
                  value={filters.user_id}
                  onChange={(e) => handleFilterChange('user_id', e.target.value)}
                  className="form-input w-full text-sm md:text-base"
                />
                <input
                  type="text"
                  placeholder="Plot ID..."
                  value={filters.plot_id}
                  onChange={(e) => handleFilterChange('plot_id', e.target.value)}
                  className="form-input w-full text-sm md:text-base"
                />
              </div>

              {/* Fourth Row - Dates */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <input
                  type="date"
                  placeholder="Start date"
                  value={filters.start_date ?
                    `${filters.start_date.slice(0,4)}-${filters.start_date.slice(4,6)}-${filters.start_date.slice(6,8)}` :
                    ''
                  }
                  onChange={(e) => handleFilterChange('start_date', e.target.value.replace(/-/g, ''))}
                  className="form-input w-full text-sm md:text-base"
                  title="Start date filter"
                />
                <input
                  type="date"
                  placeholder="End date"
                  value={filters.end_date ?
                    `${filters.end_date.slice(0,4)}-${filters.end_date.slice(4,6)}-${filters.end_date.slice(6,8)}` :
                    ''
                  }
                  onChange={(e) => handleFilterChange('end_date', e.target.value.replace(/-/g, ''))}
                  className="form-input w-full text-sm md:text-base"
                  title="End date filter"
                />
              </div>

              <div className="flex justify-center md:justify-end mt-4">
                {/* Clear Filters */}
                <button
                  onClick={() => setFilters({ search: '', user_id: '', farm_id: '', observer_name: '', plot_id: '', start_date: '', end_date: '', page: 1, limit: 20 })}
                  className="btn btn-secondary text-sm md:text-base px-4 py-2"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      Submissions Table - Desktop
      <div className="hidden md:block">
        <Table
          columns={columns}
          data={submissions}
          loading={loading}
          pagination={pagination}
          onPageChange={handlePageChange}
          onSort={handleSort}
          sortConfig={sortConfig}
          emptyMessage="No submissions found"
        />
      </div>

      {/* Submissions Cards - Mobile */}
      <div className="md:hidden space-y-3 px-2">
        {loading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="bg-white rounded-lg shadow p-4 animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-2/3"></div>
              </div>
            ))}
          </div>
        ) : submissions.length > 0 ? (
          submissions.map((submission) => (
            <div key={submission.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 w-full max-w-full overflow-hidden">
              {/* Header */}
              <div className="flex items-start justify-between mb-3 w-full">
                <div className="flex items-center space-x-2 flex-1 min-w-0 overflow-hidden">
                  <div className={`p-1.5 rounded-md flex-shrink-0 ${getHealthStatusColor(submission.plant_conditions)}`}>
                    <FileText className="w-3 h-3" />
                  </div>
                  <div className="min-w-0 flex-1 overflow-hidden">
                    <div className="font-medium text-gray-900 text-xs truncate">
                      {[submission.farm?.name, submission.plot?.name, submission.bed?.crop].filter(Boolean).join(' - ') || 'Unknown'}
                    </div>
                    <div className="text-xs text-gray-500 truncate">
                      {submission.bed?.id ? submission.bed.id.slice(0, 8) + '...' : submission.id.slice(0, 8) + '...'}
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-1 flex-shrink-0">
                  <ActionButton
                    icon={Eye}
                    label="View Details"
                    onClick={() => handleViewSubmission(submission)}
                    size="sm"
                    className="p-1"
                  />
                  <ActionMenu
                    item={submission}
                    actions={[
                      {
                        key: 'status',
                        label: 'Update Status',
                        icon: MessageSquare,
                        onClick: handleUpdateStatus,
                      },
                      ...(submission.social_post && submission.post_to_facebook ? [{
                        key: 'repost-fb',
                        label: 'Repost to Facebook',
                        icon: RefreshCw,
                        onClick: (sub) => handleRepostToSocialMedia(sub, 'facebook'),
                      }] : []),
                      ...(submission.social_post && submission.post_to_instagram ? [{
                        key: 'repost-ig',
                        label: 'Repost to Instagram',
                        icon: RefreshCw,
                        onClick: (sub) => handleRepostToSocialMedia(sub, 'instagram'),
                      }] : []),
                      ...((submission.images?.length > 0 || submission.videos?.length > 0) ? [{
                        key: 'share-tiktok-user',
                        label: 'Share to TikTok',
                        icon: RefreshCw,
                        onClick: handleUserShareToTikTok,
                      }] : []),
                      {
                        key: 'delete',
                        label: 'Delete',
                        icon: XCircle,
                        variant: 'danger',
                        onClick: handleDeleteSubmission,
                      },
                    ]}
                  />
                </div>
              </div>

              {/* Info Grid */}
              <div className="grid grid-cols-2 gap-2 text-xs w-full">
                <div className="min-w-0 overflow-hidden">
                  <div className="text-gray-500 text-xs uppercase tracking-wide">Observer</div>
                  <div className="font-medium text-gray-900 text-xs truncate">
                    {submission.observer_name || 'Unknown'}
                  </div>
                  {submission.issue_types && submission.issue_types.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {submission.issue_types.slice(0, 2).map((issueType, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-1 py-0.5 rounded text-xs bg-red-100 text-red-800"
                        >
                          {issueType}
                        </span>
                      ))}
                      {submission.issue_types.length > 2 && (
                        <span className="text-xs text-gray-500">+{submission.issue_types.length - 2} more</span>
                      )}
                    </div>
                  )}
                </div>
                <div className="min-w-0 overflow-hidden">
                  <div className="text-gray-500 text-xs uppercase tracking-wide">Date</div>
                  <div className="font-medium text-gray-900 text-xs truncate">
                    {new Date(submission.date).toLocaleDateString()}
                  </div>
                </div>
              </div>

              {/* Status Badges */}
              <div className="flex flex-wrap items-center gap-1 mt-2 pt-2 border-t border-gray-100 w-full">
                {submission.issue_types && submission.issue_types.length > 0 ? (
                  submission.issue_types.map((issueType, index) => (
                    <StatusBadge
                      key={index}
                      status={issueType}
                      variant="danger"
                    />
                  ))
                ) : (
                  (() => {
                    const healthStatus = getPlantHealthStatus(submission.plant_conditions);
                    return healthStatus.label !== 'Not Assessed' ? (
                      <StatusBadge
                        status={healthStatus.label}
                        variant={healthStatus.variant}
                      />
                    ) : null;
                  })()
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="bg-white rounded-lg shadow p-6 text-center mx-2">
            <FileText className="w-8 h-8 text-gray-400 mx-auto mb-3" />
            <p className="text-sm text-gray-500">No submissions found</p>
          </div>
        )}

        {/* Mobile Pagination */}
        {pagination && pagination.totalPages > 0 && (
          <div className="bg-white rounded-lg shadow p-3 mx-2 w-[calc(100vw-1rem)] max-w-full">
            <div className="text-xs text-gray-700 text-center mb-3">
              Showing {pagination.from || 0} to {pagination.to || 0} of {pagination.total || 0} results
            </div>
            <div className="flex items-center justify-center space-x-3">
              <button
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={!pagination.hasPrev}
                className="btn btn-secondary btn-sm disabled:opacity-50 disabled:cursor-not-allowed p-2"
              >
                <ChevronLeft className="w-3 h-3" />
              </button>
              <span className="text-xs text-gray-700 px-2 whitespace-nowrap">
                Page {pagination.currentPage} of {pagination.totalPages}
              </span>
              <button
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={!pagination.hasNext}
                className="btn btn-secondary btn-sm disabled:opacity-50 disabled:cursor-not-allowed p-2"
              >
                <ChevronRight className="w-3 h-3" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* View Submission Modal */}
      <Modal
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        title="Last Update"
        size="xl"
      >
        {selectedSubmission && (
          <div className="space-y-4 md:space-y-6 pb-4">
            

            {/* Location Information */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Farm Info */}
              {selectedSubmission.farm && (
                <div className="bg-blue-50 rounded-lg p-3 md:p-4">
                  <div className="flex items-center mb-2">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                    <h4 className="text-sm font-semibold text-blue-800">Farm</h4>
                  </div>
                  <div className="space-y-1">
                    <div className="text-sm font-medium text-blue-900 truncate">
                      {selectedSubmission.farm.name}
                    </div>
                    <div className="text-xs text-blue-600">
                      ID: {selectedSubmission.farm.id.slice(0, 8)}...
                    </div>
                    
                  </div>
                </div>
              )}

              {/* Plot Info */}
              {selectedSubmission.plot && (
                <div className="bg-green-50 rounded-lg p-3 md:p-4">
                  <div className="flex items-center mb-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                    <h4 className="text-sm font-semibold text-green-800">Plot</h4>
                  </div>
                  <div className="space-y-1">
                    <div className="text-sm font-medium text-green-900 truncate">
                      {selectedSubmission.plot.name}
                    </div>
                    <div className="text-xs text-green-600">
                      ID: {selectedSubmission.plot.id}
                    </div>
                  </div>
                </div>
              )}

              {/* Bed Info */}
              {selectedSubmission.bed && (
                <div className="bg-amber-50 rounded-lg p-3 md:p-4">
                  <div className="flex items-center mb-2">
                    <div className="w-2 h-2 bg-amber-500 rounded-full mr-2"></div>
                    <h4 className="text-sm font-semibold text-amber-800">Bed</h4>
                  </div>
                  <div className="space-y-2">
                    <div className="text-xs text-amber-600">
                      ID: {selectedSubmission.bed.id}
                    </div>
                    {selectedSubmission.bed.crop && (
                      <div className="text-sm font-medium text-amber-900">
                        Crop: {selectedSubmission.bed.crop}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Plant Info */}
              {selectedSubmission.plant && (
                <div className="bg-purple-50 rounded-lg p-3 md:p-4">
                  <div className="flex items-center mb-2">
                    <div className="w-2 h-2 bg-purple-500 rounded-full mr-2"></div>
                    <h4 className="text-sm font-semibold text-purple-800">Plant</h4>
                  </div>
                  <div className="space-y-2">
                    <div className="text-sm font-medium text-purple-900 truncate">
                      {selectedSubmission.plant.plant_common_name}
                    </div>
                    {selectedSubmission.plant.scientific_name && (
                      <div className="text-xs text-purple-600 italic truncate">
                        {selectedSubmission.plant.scientific_name}
                      </div>
                    )}
                    <div className="text-xs text-purple-600">
                      ID: {selectedSubmission.plant.id?.slice(0, 8)}...
                    </div>
                    {selectedSubmission.plant.health_status && (
                      <div className="text-xs text-purple-700">
                        Health: {selectedSubmission.plant.health_status.replace('_', ' ')}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Observer & Submission Info */}
            <div className="bg-gray-50 rounded-lg p-3 md:p-2">
              <div className="grid grid-cols-2 md:grid-cols-2 gap-2 text-sm">
                <div>
                  <div className="text-gray-500 text-xs font-medium uppercase tracking-wide">Observer</div>
                  <div className="font-medium text-gray-900">
                    {selectedSubmission.observer_name || 'Unknown'}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500 text-xs font-medium uppercase tracking-wide">Submission Date</div>
                  <div className="font-medium text-gray-900">
                    {new Date(selectedSubmission.date).toLocaleDateString()}
                  </div>
                </div>
                
              </div>
            </div>

            {/* Issue Types & Severity */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Issue Types */}
              {selectedSubmission.issue_types && selectedSubmission.issue_types.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">Issue Types</label>
                  <div className="bg-white border rounded-lg p-3 md:p-4">
                    <div className="flex flex-wrap gap-1">
                      {selectedSubmission.issue_types.map((issueType, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800"
                        >
                          {issueType}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Severity & Priority */}
              {(selectedSubmission.severity || selectedSubmission.priority) && (
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">Severity & Priority</label>
                  <div className="bg-white border rounded-lg p-3 md:p-4 space-y-2">
                    {selectedSubmission.severity && (
                      <div className="flex items-center space-x-2">
                        <span className="text-xs text-gray-500">Severity:</span>
                        <StatusBadge
                          status={selectedSubmission.severity}
                          variant={selectedSubmission.severity.toLowerCase() === 'high' ? 'danger' : selectedSubmission.severity.toLowerCase() === 'medium' ? 'warning' : 'default'}
                        />
                      </div>
                    )}
                    {selectedSubmission.priority && (
                      <div className="flex items-center space-x-2">
                        <span className="text-xs text-gray-500">Priority:</span>
                        <StatusBadge
                          status={selectedSubmission.priority}
                          variant={selectedSubmission.priority.toLowerCase() === 'high' ? 'danger' : selectedSubmission.priority.toLowerCase() === 'medium' ? 'warning' : 'default'}
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Issue Description */}
            {selectedSubmission.description && (
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">Issue Description</label>
                <div className="bg-white border rounded-lg p-3 md:p-4">
                  <p className="text-secondary-900 whitespace-pre-wrap text-sm">{selectedSubmission.description}</p>
                </div>
              </div>
            )}

            {/* Trait Measurements */}
            {selectedSubmission.trait_measurements && Object.values(selectedSubmission.trait_measurements).some(val => val > 0) && (
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-3">Trait Measurements</label>
                <div className="bg-white border rounded-lg p-3 md:p-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {selectedSubmission.trait_measurements.culm_length > 0 && (
                      <div className="text-center">
                        <div className="text-lg font-semibold text-gray-900">
                          {selectedSubmission.trait_measurements.culm_length}
                        </div>
                        <div className="text-xs text-gray-500">Culm Length</div>
                      </div>
                    )}
                    {selectedSubmission.trait_measurements.panicle_length > 0 && (
                      <div className="text-center">
                        <div className="text-lg font-semibold text-gray-900">
                          {selectedSubmission.trait_measurements.panicle_length}
                        </div>
                        <div className="text-xs text-gray-500">Panicle Length</div>
                      </div>
                    )}
                    {selectedSubmission.trait_measurements.panicles_per_hill > 0 && (
                      <div className="text-center">
                        <div className="text-lg font-semibold text-gray-900">
                          {selectedSubmission.trait_measurements.panicles_per_hill}
                        </div>
                        <div className="text-xs text-gray-500">Panicles/Hill</div>
                      </div>
                    )}
                    {selectedSubmission.trait_measurements.hills_observed > 0 && (
                      <div className="text-center">
                        <div className="text-lg font-semibold text-gray-900">
                          {selectedSubmission.trait_measurements.hills_observed}
                        </div>
                        <div className="text-xs text-gray-500">Hills Observed</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Additional Notes */}
            {selectedSubmission.notes && (
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">Additional Notes</label>
                <div className="bg-white border rounded-lg p-3 md:p-4">
                  <p className="text-secondary-900 whitespace-pre-wrap text-sm">{selectedSubmission.notes}</p>
                </div>
              </div>
            )}

            {/* Uploaded Media */}
            {(selectedSubmission.images || selectedSubmission.videos || selectedSubmission.audio) && (
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-3 md:mb-4">Uploaded Photos, Videos & Audio</label>

                {/* Photos */}
                {selectedSubmission.images && selectedSubmission.images.length > 0 && (
                  <div className="mb-4 md:mb-6">
                    <h4 className="text-sm font-medium text-secondary-600 mb-2 md:mb-3 flex items-center">
                      <Image className="w-4 h-4 mr-2" />
                      Uploaded Photos ({selectedSubmission.images.length})
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 md:gap-4">
                      {selectedSubmission.images.map((imageUrl, index) => (
                        <div
                          key={index}
                          className="relative group cursor-pointer"
                          onClick={() => window.open(imageUrl, '_blank')}
                        >
                          <img
                            src={imageUrl}
                            alt={`Submission image ${index + 1}`}
                            className="w-full h-24 md:h-32 object-cover rounded-lg hover:opacity-90 transition-opacity"
                            onError={(e) => {
                              e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjEyOCIgZmlsbD0iI2Y5ZmFmYiIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjEyOCIgZmlsbD0iI2Y5ZmFmYiIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LXNpemU9IjE0IiBmb250LWZhbWlseT0iQXJpYWwiIGZpbGw9IiM2YjczODAiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIwLjNlbSI+SW1hZ2UgTm90IEZvdW5kPC90ZXh0Pjwvc3ZnPg==';
                            }}
                          />
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all rounded-lg flex items-center justify-center">
                            <Eye className="w-5 h-5 md:w-6 md:h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Videos */}
                {selectedSubmission.videos && selectedSubmission.videos.length > 0 && (
                  <div className="mb-4 md:mb-6">
                    <h4 className="text-sm font-medium text-secondary-600 mb-2 md:mb-3 flex items-center">
                      <Play className="w-4 h-4 mr-2" />
                      Uploaded Videos ({selectedSubmission.videos.length})
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 md:gap-4">
                      {selectedSubmission.videos.map((videoUrl, index) => (
                        <div
                          key={index}
                          className="bg-secondary-50 rounded-lg p-3 md:p-4 cursor-pointer hover:bg-secondary-100 transition-colors border"
                          onClick={() => window.open(videoUrl, '_blank')}
                        >
                          <div className="flex items-center space-x-3">
                            <div className="bg-blue-100 p-2 rounded-lg flex-shrink-0">
                              <Play className="w-4 h-4 md:w-5 md:h-5 text-blue-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-secondary-900 text-sm md:text-base">Video {index + 1}</div>
                              <div className="text-xs md:text-sm text-secondary-500 truncate">
                                {videoUrl.split('/').pop() || 'video file'}
                              </div>
                            </div>
                            <div className="flex-shrink-0">
                              <Eye className="w-4 h-4 text-secondary-400" />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Audio */}
                {selectedSubmission.audio && selectedSubmission.audio.length > 0 && (
                  <div className="mb-4 md:mb-6">
                    <h4 className="text-sm font-medium text-secondary-600 mb-2 md:mb-3 flex items-center">
                      <Volume2 className="w-4 h-4 mr-2" />
                      Uploaded Audio ({selectedSubmission.audio.length})
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 md:gap-4">
                      {selectedSubmission.audio.map((audioUrl, index) => (
                        <div
                          key={index}
                          className="bg-secondary-50 rounded-lg p-3 md:p-4 cursor-pointer hover:bg-secondary-100 transition-colors border"
                          onClick={() => window.open(audioUrl, '_blank')}
                        >
                          <div className="flex items-center space-x-3">
                            <div className="bg-green-100 p-2 rounded-lg flex-shrink-0">
                              <Volume2 className="w-4 h-4 md:w-5 md:h-5 text-green-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-secondary-900 text-sm md:text-base">Audio {index + 1}</div>
                              <div className="text-xs md:text-sm text-secondary-500 truncate">
                                {audioUrl.split('/').pop() || 'audio file'}
                              </div>
                            </div>
                            <div className="flex-shrink-0">
                              <Eye className="w-4 h-4 text-secondary-400" />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Status Update Modal */}
      <Modal
        isOpen={showStatusModal}
        onClose={() => setShowStatusModal(false)}
        title="Update Submission Status"
      >
        {selectedSubmission && (
          <div className="space-y-4 min-h-0">
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                New Status
              </label>
              <select
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
                className="form-select w-full"
                required
              >
                <option value="submitted">Submitted</option>
                <option value="reviewed">Reviewed</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Status Note (Optional)
              </label>
              <textarea
                value={statusNote}
                onChange={(e) => setStatusNote(e.target.value)}
                rows={3}
                className="form-input w-full resize-none"
                placeholder="Add a note about this status change..."
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={() => setShowStatusModal(false)}
                className="btn btn-secondary"
                disabled={statusLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleStatusUpdate}
                className="btn btn-primary"
                disabled={statusLoading}
              >
                {statusLoading ? 'Updating...' : 'Update Status'}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Submission"
        message={`Are you sure you want to delete the submission "${selectedSubmission?.title}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
      />
    </div>
  );
};

export default SubmissionsPage;